import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Card, CardContent, Chip, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PageHeader from '../../components/common/PageHeader';
import FormSkeleton from '../../components/common/FormSkeleton';
import TipTapEditor from '../../components/common/TipTapEditor';
import ScreenshotGallery from '../../components/common/ScreenshotGallery';
import { sandboxService } from '../../services/sandboxService';
import { SharedFinding } from '../../types';
import { extractApiError } from '../../utils/errorUtils';
import { formatSeverityLower, getKritikalitaetChipSx } from '../../utils/severity';

/** Nur-Lesen-Ansicht eines freigegebenen Findings (eingeschraenkte Sitzung). */
const SharedFindingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [finding, setFinding] = useState<SharedFinding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    sandboxService.shared
      .finding(parseInt(id, 10))
      .then((f) => {
        setFinding(f);
        setError(null);
      })
      .catch((e) => setError(extractApiError(e, 'Finding nicht freigegeben')))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <FormSkeleton />;
  if (error || !finding) {
    return (
      <Box>
        <PageHeader title="Freigegebenes Finding" backTo="/sandbox" />
        <Alert severity="error">{error || 'Finding nicht freigegeben'}</Alert>
      </Box>
    );
  }

  const labelSx = { width: 160, color: 'text.secondary', fontWeight: 600, fontSize: '0.8rem', border: 0, py: 1, pl: 0 };
  const valueSx = { border: 0, py: 1, pr: 0 };
  const backTo = finding.freigabe_typ === 'audit' ? `/sandbox/shared/audits/${finding.audit_id}` : '/sandbox';

  return (
    <Box>
      <PageHeader title={finding.titel} subtitle={`Freigegeben aus Audit „${finding.audit_name}“ – nur lesen`} backTo={backTo} />
      <Alert icon={<LockIcon fontSize="inherit" />} severity="warning" sx={{ mb: 2 }}>
        Nur lesen: Dieses Finding wurde {finding.freigabe_typ === 'audit' ? 'über sein Audit' : 'einzeln'} vom Hauptaccount
        freigegeben. Es ist kein Finding Ihrer isolierten Umgebung und kann hier nicht bearbeitet werden.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={labelSx}>Audit</TableCell>
                <TableCell sx={valueSx}>{finding.audit_name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={labelSx}>Kritikalität</TableCell>
                <TableCell sx={valueSx}>
                  {finding.kritikalitaet ? <Chip size="small" label={formatSeverityLower(finding.kritikalitaet)} sx={getKritikalitaetChipSx(finding.kritikalitaet)} /> : '–'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={labelSx}>CVSS 3.1</TableCell>
                <TableCell sx={valueSx}>
                  {finding.cvss31_score != null ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip label={finding.cvss31_score.toFixed(1)} size="small" color="primary" sx={{ fontWeight: 700 }} />
                      {finding.cvss31_vector && (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{finding.cvss31_vector}</Typography>
                      )}
                    </Box>
                  ) : '–'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={labelSx}>Schwachstellenklasse</TableCell>
                <TableCell sx={valueSx}>{finding.schwachstellenklasse?.name_de || '–'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={labelSx}>CWE</TableCell>
                <TableCell sx={valueSx}>{finding.cwe ? `CWE-${finding.cwe.id}: ${finding.cwe.name}` : '–'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={labelSx}>Host</TableCell>
                <TableCell sx={valueSx}>{finding.host || '–'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={labelSx}>IP-Adresse</TableCell>
                <TableCell sx={valueSx}><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{finding.ip_adresse || '–'}</Typography></TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={labelSx}>Risikoszenarien</TableCell>
                <TableCell sx={valueSx}>{finding.risikoszenarien.map((r) => r.name_de).join(', ') || '–'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={labelSx}>Tools</TableCell>
                <TableCell sx={valueSx}>{finding.tools.map((t) => t.name).join(', ') || '–'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TipTapEditor label="Allgemeine Beschreibung" content={finding.allgemeine_beschreibung || ''} editable={false} minHeight={80} />
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TipTapEditor label="Detailbeschreibung" content={finding.detailbeschreibung || ''} editable={false} minHeight={80} />
        </CardContent>
      </Card>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TipTapEditor label="Mitigationsmassnahmen" content={finding.mitigationsmassnahmen || ''} editable={false} minHeight={80} />
        </CardContent>
      </Card>

      <ScreenshotGallery
        schwachstelleId={finding.id}
        editable={false}
        fileApi={sandboxService.shared.fileApi}
        allowDelete={false}
        referenceHtml={[finding.allgemeine_beschreibung, finding.detailbeschreibung, finding.mitigationsmassnahmen].filter(Boolean).join(' ')}
      />
    </Box>
  );
};

export default SharedFindingPage;
